// Product categories data for Nago International — uses real images from company profile.
import {
  Droplet,
  Beaker,
  Cog,
  Zap,
  CircleDashed,
  ShieldCheck,
  Package,
  FlaskConical,
  Wrench,
} from "lucide-react";

export const PRODUCT_CATEGORIES = [
  {
    slug: "industrial-lubricants",
    title: "Industrial Lubricants & Specialty Oils",
    short:
      "Hydraulic, gear, spindle, compressor and specialty oils, greases and maintenance lubricants across ISO VG, SAE and NLGI grades.",
    brands: ["Velvex", "Divyol"],
    Icon: Droplet,
    image: "/images/products/velvex/hydrovel-68-210l.png",
    highlights: [
      "Hydraulic oils VG 10 to VG 460",
      "Industrial gear oils VG 68 – VG 680",
      "Spindle & way oils, slideway lubricants",
      "Multipurpose, EP and specialty greases",
    ],
    tables: [
      {
        title: "Hydraulic Oil — ISO VG Grades",
        columns: ["ISO VG Grade", "Application", "Common Pack Sizes"],
        rows: [
          ["VG 32", "Precision machine hydraulics", "26 L, 210 L"],
          ["VG 46", "Standard hydraulic systems", "26 L, 210 L"],
          ["VG 68", "Heavy-duty hydraulics, elevated temp.", "26 L, 210 L"],
          ["VG 100", "Industrial systems, gearboxes", "210 L"],
          ["VG 220 – 460", "Industrial gear oil applications", "210 L"],
        ],
      },
      {
        title: "Greases",
        columns: ["Grade", "Type", "Typical Use"],
        rows: [
          ["MP-2 / MP-3", "Multipurpose Lithium", "General bearings, hinges"],
          ["AP-2 / AP-3", "Automotive", "Vehicle chassis, wheel bearings"],
          ["EP-0 to EP-3", "Extreme Pressure", "Heavy-load industrial gears"],
          ["EP-X", "Extended Performance", "Long-life relubrication"],
        ],
      },
    ],
  },
  {
    slug: "metal-working-fluids",
    title: "Metal Working Fluids & Coolants",
    short:
      "Semi-synthetic and synthetic coolants, cutting, honing and metal-forming fluids for productive, cleaner machining.",
    brands: ["Velvex", "Divyol", "Emulsichem"],
    Icon: Beaker,
    image: "/images/products/velvex/synth-cut-ep25-plus-210l.png",
    highlights: [
      "Soluble cutting oils (semi-synthetic & synthetic)",
      "Neat cutting oils for tapping, honing, gun-drilling",
      "Rust preventives and slideway lubricants",
      "Custom pack sizes: 20 L, 26 L, 50 L, 210 L",
    ],
    tables: [
      {
        title: "Popular Cutting Fluids",
        columns: ["Product", "Type", "Pack"],
        rows: [
          [
            "Velvex Semi-Synthetic Prime",
            "Soluble cutting oil",
            "20 L / 26 L / 210 L",
          ],
          ["Velvex Synth Cut EP-25 Plus", "Semi-synthetic HP", "20 L / 210 L"],
          [
            "Velvex Synth Cut EP-70",
            "High-performance synthetic",
            "26 L / 210 L",
          ],
          ["Divyol Cut-400", "Soluble cutting oil", "26 L / drum"],
          ["Divyol Multicut-B", "Multi-metal cutting oil", "50 L"],
          ["Divyol Hone Oil 981", "Honing oil", "20 L"],
        ],
      },
    ],
  },
  {
    slug: "cutting-tools-band-saw",
    title: "Cutting Tools & Band Saw Blades",
    short:
      "Bi-metal band saw blades and circular saw cutters for structural, production and high-speed metal cutting.",
    brands: ["Kolchen", "Evershine"],
    Icon: Zap,
    image: "/images/products/kolchen/band-saw-blade.jpeg",
    highlights: [
      "Kolchen Bi-metal Band Saw Blades",
      "Series: Structut, Structut Plus, Supreme, Xpert",
      "Evershine Circular Saw Cutters",
      "Sizes 250 mm to 460 mm",
    ],
    tables: [
      {
        title: "Kolchen Band Saw Blade Sizes",
        columns: ["Width × Thickness (mm)", "Recommended Use"],
        rows: [
          ["19 × 0.9", "Small production saws"],
          ["27 × 0.9", "Light structural cutting"],
          ["34 × 1.1", "General purpose"],
          ["41 × 1.3", "Medium-duty steel cutting"],
          ["54 × 1.6", "Heavy-duty solid bar"],
          ["67 × 1.6", "Large bar / heavy sections"],
          ["80 × 1.6", "Extra heavy-duty"],
        ],
      },
      {
        title: "Evershine Circular Saw Cutter",
        columns: ["Diameter (mm)"],
        rows: [["250"], ["285"], ["315"], ["360"], ["420"], ["460"]],
      },
    ],
  },
  {
    slug: "precision-tooling",
    title: "Carbide & Precision Tooling",
    short:
      "Solid carbide tools, ISO inserts, tool holders, milling, drilling, boring, threading and reaming solutions.",
    brands: [
      "BPT (Birla Precision)",
      "Indian Tools (Dagger)",
      "CarboMach",
      "Birla Durotool",
    ],
    Icon: Cog,
    image: "/images/products/bpt/iso-turning-insert.png",
    highlights: [
      "HSS twist drills, taps, reamers, milling cutters",
      "Solid carbide drills, end mills, milling solutions",
      "ISO turning & milling inserts (CNMG, DNMG, TNMG…)",
      "Panther Taps — Spiral, Roll Form, Cast Iron",
    ],
    tables: [
      {
        title: "Popular HSS & Carbide Ranges",
        columns: ["Product", "Range"],
        rows: [
          ["HSS Twist Drills", "1 – 25 mm"],
          ["HSS Centre Drills (Type A / B)", "1 – 10 mm"],
          ["Screwing Taps", "M3 – M20"],
          ["Reamers", "3 – 25 mm"],
          ["Solid Carbide End Mills", "2 – 20 mm"],
          ["Solid Carbide Drills", "3 – 20 mm"],
        ],
      },
      {
        title: "ISO Turning / Milling Inserts",
        columns: ["Series", "Operation"],
        rows: [
          ["CNMG / DNMG / TNMG / WNMG / SNMG / VNMG", "Turning"],
          ["APKT / SEKT / RPMT", "Milling"],
          ["VBMT / CCMT / DCMT", "Finish turning"],
        ],
      },
    ],
  },
  {
    slug: "abrasives",
    title: "Abrasives & Surface Finishing",
    short:
      "Cut-off & chopsaw wheels, depressed-centre and ultra-thin discs, grinding and finishing products.",
    brands: ["Birla Durotool"],
    Icon: CircleDashed,
    image: "/images/products/birla-durotool/depressed-centre-disc.jpeg",
    highlights: [
      'Ultra-thin cutting wheels 4" / 5" / 7"',
      'Depressed centre discs 4" / 5" / 7"',
      'Chopsaw wheels 14" (355 mm) / 16" (405 mm)',
      "TCT circular saws 250 – 450 mm",
    ],
    tables: [
      {
        title: "Popular Abrasive Sizes",
        columns: ["Product", "Sizes"],
        rows: [
          ["Ultra-thin Cutting Wheel", '4" / 5" / 7"'],
          ["Depressed Centre Disc", '4" / 5" / 7"'],
          ["Chopsaw Wheel", '14" (355) / 16" (405)'],
          ["TCT Circular Saw", "250 / 300 / 350 / 400 / 450 mm"],
          ["Annular Cutters", "12 – 60 mm"],
          ["Carbide Tip Drills", "3 – 25 mm"],
        ],
      },
    ],
  },
  {
    slug: "safety-ppe",
    title: "Safety Equipment & PPE",
    short:
      "Hand protection, safety gloves, masks, PPE and safety consumables for safe, efficient operations.",
    brands: ["Argus", "Torpedo", "and other trusted brands"],
    Icon: ShieldCheck,
    image: "/images/products/generic/safety-materials.jpeg",
    highlights: [
      "Examination gloves, nitrile gripper gloves",
      "Leather & leather-plus-cotton hand gloves",
      "Woollen hand gloves (40 / 70 / 80 GSM)",
      "Safety masks (yellow / green)",
    ],
    tables: [
      {
        title: "Safety Consumables",
        columns: ["Product", "Variants"],
        rows: [
          ["Examination Gloves", "Blue"],
          ["Woollen Hand Gloves", "40 / 70 / 80 GSM"],
          ["Nitrile Gripper Gloves", "Standard, heavy-duty"],
          ["Leather Gloves", "Full leather / Leather + Cotton"],
          ["Safety Masks", "Yellow / Green"],
        ],
      },
    ],
  },
  {
    slug: "packaging",
    title: "Packaging Materials",
    short:
      "Bubble and EPE foam sheets, stretch films, tapes, PET straps and customized packaging for storage and transit.",
    brands: ["Trusted packaging partners"],
    Icon: Package,
    image: "/images/products/generic/packaging-materials.jpeg",
    highlights: [
      'Stretch films 3" – 18"',
      "Air bubble sheets 50 – 120 GSM",
      "EPE foam sheets 2 – 75 mm",
      "PET straps, gatta patti, clips",
    ],
    tables: [
      {
        title: "Stretch Films & Bubble",
        columns: ["Product", "Sizes"],
        rows: [
          ["Stretch Film", '3" / 4" / 6" / 8" / 12" / 18"'],
          ["Air Bubble Sheet", "1000 x 50 / 70 / 120 GSM · 1500 x 50 GSM"],
          ["Foam Sheet", "2 / 5 / 6 / 12 / 25 / 40 / 50 / 75 mm"],
        ],
      },
      {
        title: "Strapping & Bundling",
        columns: ["Product", "Sizes"],
        rows: [
          ["Green PET Strap", "12 mm / 16 mm"],
          ["Green Gatta Patti", "12 mm / 16 mm"],
          ["Green Gatta Patti Clip", "12 mm"],
          ["Stepping Patti (White)", "12 mm"],
          ["Industrial Tapes", "Teflon, BOPP Brown, BOPP Transparent"],
          ["Protective", "Silica Gel, VCI Bags"],
        ],
      },
    ],
  },
  {
    slug: "industrial-chemicals",
    title: "Industrial Chemicals & Maintenance",
    short:
      "Rust prevention, cleaning chemicals, maintenance sprays and anti-corrosion products.",
    brands: ["Molygraph", "Divyol"],
    Icon: FlaskConical,
    image: "/images/products/divyol/hone-oil-981.png",
    highlights: [
      "Molygraph Rustolean & Lube Spray",
      "Molygraph Check Paste",
      "Anti-rust oils (OB 60, DW 175)",
      "Cotton waste, marker pens, hessian fabric",
    ],
    tables: [
      {
        title: "Maintenance Products",
        columns: ["Product", "Purpose"],
        rows: [
          [
            "Molygraph Rustolean & Lube Spray",
            "Corrosion protection & lubrication",
          ],
          ["Molygraph Check Paste", "Marking / inspection paste"],
          ["Divyol Antirust OB 60", "Rust preventive"],
          ["Divyol Antirust DW 175", "Rust preventive (heavy duty)"],
          ["Cotton Waste (Dhaga)", "Wiping / cleaning"],
          ["Desiccants Super Gel (5 g)", "Moisture control"],
        ],
      },
    ],
  },
  {
    slug: "custom-machines",
    title: "Custom Machines & Engineering",
    short:
      "Application-specific machines and engineering solutions sourced through trusted manufacturing partners.",
    brands: ["Sourced via trusted manufacturing partners"],
    Icon: Wrench,
    image: "/images/products/generic/custom-machines-engineering.png",
    highlights: [
      "Made-to-spec industrial machines",
      "Process automation & tooling",
      "Design, sourcing & delivery managed end-to-end",
      "Backed by network of manufacturing associates",
    ],
    tables: [],
  },
];

export const CATEGORY_OPTIONS = PRODUCT_CATEGORIES.map((c) => ({
  value: c.slug,
  label: c.title,
}));
